import { useState, useEffect } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Settings, Save, Clock, Calendar, CalendarRange } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { placeholderForSubcat } from '@/lib/businessTypes';
import TeamManagementCard from '@/components/admin/TeamManagementCard';
import OnlineBookingCard from '@/components/admin/OnlineBookingCard';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const DAY_NAMES = ['Pazartesi', 'Sal\u0131', '\u00c7ar\u015famba', 'Per\u015fembe', 'Cuma', 'Cumartesi', 'Pazar'];

export const AdminSettings = () => {
  const { user: currentUser, checkAuth } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;

  const [businessName, setBusinessName] = useState('');
  const [workingHoursStart, setWorkingHoursStart] = useState('08:00');
  const [workingHoursEnd, setWorkingHoursEnd] = useState('20:00');
  const [lunchBreakStart, setLunchBreakStart] = useState('');
  const [lunchBreakEnd, setLunchBreakEnd] = useState('');
  const [workingDays, setWorkingDays] = useState([0, 1, 2, 3, 4, 5]);
  const [slotDuration, setSlotDuration] = useState(30);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Appointment category tree (new 2026.05)
  const [categories, setCategories] = useState([]);
  const [appointmentCategory, setAppointmentCategory] = useState('');
  const [appointmentSubcategory, setAppointmentSubcategory] = useState('');

  const labels = currentUser?.labels || { people: 'Kullanıcılar', payment: 'Ödeme', identifier: 'Numara' };
  const isAppointmentType = ['barber', 'beauty', 'dental'].includes(currentUser?.business_type);
  const supportsOnlineBooking = ['barber', 'beauty', 'dental', 'gym'].includes(currentUser?.business_type)
    || !!currentUser?.appointment_category;
  const showWorkingHours = ['barber', 'beauty', 'dental', 'gym'].includes(currentUser?.business_type);

  useEffect(() => {
    fetchSettings();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/appointments/categories`, { withCredentials: true });
      setCategories(data.categories || []);
    } catch { /* ignore */ }
  };

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/business-settings`, { withCredentials: true });
      setBusinessName(data.business_name || '');
      setAppointmentCategory(data.appointment_category || '');
      setAppointmentSubcategory(data.appointment_subcategory || '');
      if (data.settings) {
        setWorkingHoursStart(data.settings.working_hours_start || '08:00');
        setWorkingHoursEnd(data.settings.working_hours_end || '20:00');
        setLunchBreakStart(data.settings.lunch_break_start || '');
        setLunchBreakEnd(data.settings.lunch_break_end || '');
        setWorkingDays(data.settings.working_days || [0, 1, 2, 3, 4, 5]);
        setSlotDuration(data.settings.slot_duration || 30);
      }
    } catch (error) {
      console.error('Ayarlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!businessName.trim()) {
      toast.error('İşletme adi boş olamaz');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/business-settings`, {
        business_name: businessName.trim(),
        working_hours_start: workingHoursStart,
        working_hours_end: workingHoursEnd,
        lunch_break_start: lunchBreakStart,
        lunch_break_end: lunchBreakEnd,
        working_days: workingDays,
        slot_duration: slotDuration,
        appointment_category: appointmentCategory || null,
        appointment_subcategory: appointmentSubcategory || null,
      }, { withCredentials: true });
      await checkAuth();
      toast.success('Ayarlar güncellendi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const activeSubcats = categories.find((c) => c.key === appointmentCategory)?.subcategories || [];

  // ---------- Custom services sub-component ----------
  function CustomServicesCard() {
    const [items, setItems] = useState([]);
    const [loadingItems, setLoadingItems] = useState(false);
    const [newSvc, setNewSvc] = useState({ name: '', duration_min: '', default_price: '' });
    const [adding, setAdding] = useState(false);
    // Placeholder reacts to the currently-selected service area so the example
    // matches the sector (e.g. "Diş Beyazlatma" for a dental clinic, not a hair salon term).
    const dynamicPlaceholder = placeholderForSubcat(appointmentSubcategory);

    const load = async () => {
      setLoadingItems(true);
      try {
        const { data } = await axios.get(`${API_URL}/api/custom-services`, { withCredentials: true });
        setItems(data.items || []);
      } catch { /* ignore */ } finally { setLoadingItems(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const handleAdd = async (e) => {
      e.preventDefault();
      if (!newSvc.name.trim() || newSvc.name.trim().length < 2) {
        return toast.error('Hizmet adı en az 2 karakter olmalı');
      }
      setAdding(true);
      try {
        const payload = { name: newSvc.name.trim() };
        const dm = parseInt(newSvc.duration_min, 10);
        const dp = parseFloat(newSvc.default_price);
        if (Number.isFinite(dm) && dm > 0) payload.duration_min = dm;
        if (Number.isFinite(dp) && dp >= 0) payload.default_price = dp;
        await axios.post(`${API_URL}/api/custom-services`, payload, { withCredentials: true });
        toast.success('Hizmet eklendi');
        setNewSvc({ name: '', duration_min: '', default_price: '' });
        load();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Eklenemedi');
      } finally { setAdding(false); }
    };

    const handleDelete = async (id, name) => {
      if (!window.confirm(`"${name}" hizmeti silinsin mi?`)) return;
      try {
        await axios.delete(`${API_URL}/api/custom-services/${id}`, { withCredentials: true });
        toast.success('Silindi');
        load();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Silinemedi');
      }
    };

    return (
      <Card className="border border-[#E5E5E5]" data-testid="custom-services-card">
        <CardHeader>
          <CardTitle className="text-xl text-[#0F172A] flex items-center gap-2">
            <Settings className="w-5 h-5" strokeWidth={1.5} />
            Hizmet ve Fiyat Listem
          </CardTitle>
          <p className="text-xs text-[#595959] mt-1">
            Sunduğunuz hizmetleri ekleyin. Süre ve ücret <strong>opsiyoneldir</strong> — sadece doldurduğunuz alanlar online randevu sayfanızda gösterilir.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_auto] gap-2 items-end mb-4">
            <div><Label className="text-xs">Hizmet adı</Label><Input value={newSvc.name} onChange={(e) => setNewSvc({ ...newSvc, name: e.target.value })} placeholder={dynamicPlaceholder} data-testid="cs-name" /></div>
            <div><Label className="text-xs">Süre (dk) <span className="text-[#9CA3AF]">opsiyonel</span></Label><Input type="number" min="1" placeholder="—" value={newSvc.duration_min} onChange={(e) => setNewSvc({ ...newSvc, duration_min: e.target.value })} data-testid="cs-duration" /></div>
            <div><Label className="text-xs">Ücret (₺) <span className="text-[#9CA3AF]">opsiyonel</span></Label><Input type="number" min="0" step="0.01" placeholder="—" value={newSvc.default_price} onChange={(e) => setNewSvc({ ...newSvc, default_price: e.target.value })} data-testid="cs-price" /></div>
            <Button type="submit" disabled={adding} className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white" data-testid="cs-submit">Ekle</Button>
          </form>

          {loadingItems ? (
            <p className="text-sm text-[#999] text-center py-4">Yükleniyor...</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-[#999] text-center py-4">Henüz hizmet eklemediniz.</p>
          ) : (
            <div className="border border-[#E5E5E5] rounded-md divide-y divide-[#F3F4F6]">
              {items.map((s) => {
                const hasDur = s.duration_min !== null && s.duration_min !== undefined && s.duration_min > 0;
                const hasPrice = s.default_price !== null && s.default_price !== undefined && s.default_price > 0;
                const meta = [
                  hasDur ? `${s.duration_min} dk` : null,
                  hasPrice ? `${Number(s.default_price).toLocaleString('tr-TR')} ₺` : null,
                ].filter(Boolean).join(' · ') || <span className="italic text-[#9CA3AF]">süre/ücret belirtilmemiş</span>;
                return (
                  <div key={s.id} className="flex items-center justify-between gap-2 px-3 py-2" data-testid={`cs-row-${s.id}`}>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-[#0F172A]">{s.name}</div>
                      <div className="text-xs text-[#595959]">{meta}</div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id, s.name)} className="text-red-600 hover:bg-red-50" data-testid={`cs-delete-${s.id}`}>Sil</Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  const toggleDay = (dayIndex) => {
    setWorkingDays(prev =>
      prev.includes(dayIndex) ? prev.filter(d => d !== dayIndex) : [...prev, dayIndex].sort()
    );
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="text-center py-12 text-[#595959]">Yükleniyor...</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      
      
        <div className="max-w-2xl mx-auto">
          <div className="mb-8 flex items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]" data-testid="settings-page-title">
                İşletme Ayarları
              </h1>
              <p className="text-base text-[#595959] mt-2">İşletme bilgilerinizi ve çalışma saatlerinizi yönetin</p>
            </div>
            <LanguageSwitcher />
          </div>

          <div className="space-y-6">
            {/* Business Name */}
            <Card className="border border-[#E5E5E5]" data-testid="business-name-card">
              <CardHeader>
                <CardTitle className="text-xl text-[#0F172A] flex items-center gap-2">
                  <Settings className="w-5 h-5" strokeWidth={1.5} />
                  İşletme Adı
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div>
                  <Label className="text-sm text-[#595959]">İşletme Adı</Label>
                  <Input
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="Örn: ABC Sitesi, XYZ Spor Salonu"
                    data-testid="business-name-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Appointment category — only relevant for appointment-style businesses */}
            {(isAppointmentType || showWorkingHours) && (
              <Card className="border border-[#E5E5E5]" data-testid="appointment-category-card">
                <CardHeader>
                  <CardTitle className="text-xl text-[#0F172A] flex items-center gap-2">
                    <CalendarRange className="w-5 h-5" strokeWidth={1.5} />
                    İşletme Türü ve Hizmetler
                  </CardTitle>
                  <p className="text-xs text-[#595959] mt-1">İşletmenize uygun hizmet listesini seçin ve randevu ekranında kullanın.</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm text-[#595959]">İşletme Türü</Label>
                    <Select
                      value={appointmentCategory || 'none'}
                      onValueChange={(v) => {
                        const next = v === 'none' ? '' : v;
                        setAppointmentCategory(next);
                        // reset service area when business type changes
                        setAppointmentSubcategory('');
                      }}
                    >
                      <SelectTrigger data-testid="appt-category-select"><SelectValue placeholder="İşletme türü seçin" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Seçilmedi</SelectItem>
                        {categories.map((c) => (
                          <SelectItem key={c.key} value={c.key} data-testid={`appt-cat-opt-${c.key}`}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {activeSubcats.length > 0 && (
                    <div>
                      <Label className="text-sm text-[#595959]">Hizmet Alanı</Label>
                      <Select value={appointmentSubcategory || 'none'} onValueChange={(v) => setAppointmentSubcategory(v === 'none' ? '' : v)}>
                        <SelectTrigger data-testid="appt-subcategory-select"><SelectValue placeholder="Hizmet alanı seçin" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Seçilmedi</SelectItem>
                          {activeSubcats.map((s) => (
                            <SelectItem key={s.key} value={s.key} data-testid={`appt-sub-opt-${s.key}`}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {appointmentCategory && !appointmentSubcategory && (
                    <p className="text-xs text-amber-700">⚠ Hizmet alanı seçilmeden hizmet listesi yüklenmez.</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Custom services management */}
            {(isAppointmentType || showWorkingHours) && (
              <CustomServicesCard />
            )}

            {/* Working Hours - Only for appointment/gym types */}
            {showWorkingHours && (
            <>
            <Card className="border border-[#E5E5E5]" data-testid="working-hours-card">
              <CardHeader>
                <CardTitle className="text-xl text-[#0F172A] flex items-center gap-2">
                  <Clock className="w-5 h-5" strokeWidth={1.5} />
                  Çalışma Saatleri
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-[#595959]">Açılış Saati</Label>
                    <Input
                      type="time"
                      value={workingHoursStart}
                      onChange={(e) => setWorkingHoursStart(e.target.value)}
                      data-testid="working-hours-start"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[#595959]">Kapanış Saati</Label>
                    <Input
                      type="time"
                      value={workingHoursEnd}
                      onChange={(e) => setWorkingHoursEnd(e.target.value)}
                      data-testid="working-hours-end"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-[#595959]">Öğle Arası Başlangıç</Label>
                    <Input
                      type="time"
                      value={lunchBreakStart}
                      onChange={(e) => setLunchBreakStart(e.target.value)}
                      placeholder="Opsiyonel"
                      data-testid="lunch-break-start"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-[#595959]">Öğle Arası Bitiş</Label>
                    <Input
                      type="time"
                      value={lunchBreakEnd}
                      onChange={(e) => setLunchBreakEnd(e.target.value)}
                      placeholder="Opsiyonel"
                      data-testid="lunch-break-end"
                    />
                  </div>
                </div>

                {isAppointmentType && (
                  <div>
                    <Label className="text-sm text-[#595959]">Randevu Süresi (dakika)</Label>
                    <Input
                      type="number"
                      min={5}
                      max={240}
                      value={slotDuration}
                      onChange={(e) => setSlotDuration(parseInt(e.target.value) || 30)}
                      data-testid="slot-duration"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Working Days */}
            <Card className="border border-[#E5E5E5]" data-testid="working-days-card">
              <CardHeader>
                <CardTitle className="text-xl text-[#0F172A] flex items-center gap-2">
                  <Calendar className="w-5 h-5" strokeWidth={1.5} />
                  Çalışma Günleri
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {DAY_NAMES.map((dayName, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`day-toggle-${idx}`}>
                      <span className="text-sm font-medium text-[#1A1A1A]">{dayName}</span>
                      <Switch
                        checked={workingDays.includes(idx)}
                        onCheckedChange={() => toggleDay(idx)}
                        data-testid={`day-switch-${idx}`}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            </>
            )}

            {/* Team Management - Owner only */}
            {currentUser?.role !== 'staff' && <TeamManagementCard />}

            {/* Online Booking - Owner only, appointment-supporting businesses */}
            {currentUser?.role !== 'staff' && supportsOnlineBooking && <OnlineBookingCard />}

            {/* Terminology Info - Read only */}
            <Card className="border border-[#E5E5E5] bg-[#F9F8F6]" data-testid="terminology-info-card">
              <CardHeader>
                <CardTitle className="text-lg text-[#2563EB]">Terminoloji</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-xs text-[#595959] mb-1">Kişiler</p>
                    <p className="font-medium text-[#1A1A1A]" data-testid="info-people">{labels.people}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#595959] mb-1">Ödeme Türü</p>
                    <p className="font-medium text-[#1A1A1A]" data-testid="info-payment">{labels.payment}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-[#595959] mb-1">Tanımlayıcı</p>
                    <p className="font-medium text-[#1A1A1A]" data-testid="info-identifier">{labels.identifier}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white gap-2 w-full h-12 text-base"
              data-testid="save-settings-button"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </div>
      
    </PageLayout>
  );
};
